import { Check } from 'lucide-react';
import { useSetupStore } from '@/stores/setupStore';
import { useBrowserStore } from '@/stores/browserStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { cn } from '@/lib/utils';

interface Step {
  label: string;
  done: boolean;
}

/**
 * Compact progress checklist pinned above the guided Zoom-setup chat. Answers
 * "how much is left?" at a glance — the chat bubbles narrate, this orients.
 * Mirrors the Account page's app-lifecycle checklist (✓ / current / upcoming).
 *
 * Done-signals per step, sturdiest available first: `connected` and granted
 * scopes are durable (server-derived, survive reload); appCreated/credsCaptured
 * are ephemeral mid-flow flags (reload mid-run collapses them into whatever the
 * durable signals say — never wrong, at worst less granular).
 */
export function SetupStepper() {
  const accessLevel = useSetupStore((s) => s.accessLevel);
  const connected = useSetupStore((s) => s.connected);
  const appCreated = useSetupStore((s) => s.appCreated);
  const credsCaptured = useSetupStore((s) => s.credsCaptured);
  const infoFilled = useSetupStore((s) => s.infoFilled);
  const scopesConfirmed = useSetupStore((s) => s.scopesConfirmed);
  const activated = useSetupStore((s) => s.activated);
  const zoomSignedIn = useBrowserStore((s) => s.zoomSignedIn);
  const connections = useConnectionStore((s) => s.connections);

  const scopesReady = connections.some((c) => (c.scopes?.length ?? 0) > 0);

  // A verified token (scopesReady) implies the whole editor phase succeeded;
  // `connected` implies everything through identity capture. Ephemeral flags
  // fill in the mid-flow granularity between those durable anchors.
  const steps: Step[] = [
    { label: 'Choose access level', done: !!accessLevel || connected },
    { label: 'Sign in to Zoom', done: zoomSignedIn || appCreated || connected },
    { label: 'Create connection app', done: appCreated || credsCaptured || connected },
    { label: 'Save credentials', done: credsCaptured || connected },
    { label: 'Fill app info', done: infoFilled || activated || scopesReady },
    { label: accessLevel === 'writer' ? 'Add read + write scopes' : 'Add read scopes', done: scopesConfirmed || scopesReady },
    { label: 'Activate app', done: activated || scopesReady },
    { label: 'Connect & verify', done: connected && scopesReady },
  ];

  // Setup fully finished — nothing left to orient toward.
  if (connected && scopesReady) return null;

  const currentIdx = steps.findIndex((s) => !s.done);
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="mx-4 mt-1 rounded-lg border border-border/60 bg-card/60 px-3 py-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">Zoom setup</span>
        <span className="text-xs text-muted-foreground">{doneCount}/{steps.length}</span>
      </div>
      <ol className="mt-2 space-y-1">
        {steps.map((step, i) => {
          const isCurrent = i === currentIdx;
          return (
            <li key={step.label} className="flex items-center gap-2 text-xs">
              {step.done ? (
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500/20">
                  <Check className="h-2.5 w-2.5 text-emerald-400" />
                </span>
              ) : isCurrent ? (
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />
                </span>
              ) : (
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <span className="h-2 w-2 rounded-full border border-muted-foreground/40" />
                </span>
              )}
              <span
                className={cn(
                  step.done && 'text-muted-foreground line-through decoration-muted-foreground/40',
                  isCurrent && 'font-medium text-foreground',
                  !step.done && !isCurrent && 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
